import React, { useState } from 'react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import { 
  TrendingUp, TrendingDown, DollarSign, Package, AlertTriangle, 
  Calendar as CalendarIcon, Download, Loader2, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { useCM1Report, useCM1Trend } from '@/hooks/useCM1Report';
import { exportCM1ReportToExcel, type ProductCM1Data } from '@/services/cm1ReportService';
import { formatCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils';

const CHART_COLORS = {
  revenue: 'hsl(var(--chart-1))',
  cogs: 'hsl(var(--chart-2))',
  damage: 'hsl(var(--chart-3))',
  cm1: 'hsl(var(--chart-4))',
};

const CM1Report: React.FC = () => {
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: startOfMonth(new Date()),
    to: new Date()
  });

  const { data: reportData, isLoading, error } = useCM1Report(dateRange.from, dateRange.to);
  const { data: trendData } = useCM1Trend(dateRange.from, dateRange.to);

  const handleExport = () => {
    if (reportData) {
      const filename = `CM1_Report_${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}`;
      exportCM1ReportToExcel(reportData, filename);
    }
  };

  const setPresetRange = (days: number) => {
    setDateRange({
      from: subDays(new Date(), days),
      to: new Date()
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        Error loading CM1 report: {(error as Error).message}
      </div>
    );
  }

  const summary = reportData?.summary;
  const products = reportData?.products || [];

  // Prepare chart data for cost breakdown
  const costBreakdownData = [
    { name: 'COGS', value: summary?.totalCogs || 0, color: CHART_COLORS.cogs },
    { name: 'Damage Loss', value: summary?.totalDamageLoss || 0, color: CHART_COLORS.damage },
  ];

  // Top performers and underperformers
  const topPerformers = products.slice(0, 5);
  const underperformers = [...products].sort((a, b) => a.cm1_margin - b.cm1_margin).slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header with date controls */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-primary" />
            CM1 Profitability Report
          </h1>
          <p className="text-muted-foreground">
            Revenue - COGS - Damage Loss = Contribution Margin 1
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPresetRange(7)}>7D</Button>
            <Button variant="outline" size="sm" onClick={() => setPresetRange(30)}>30D</Button>
            <Button variant="outline" size="sm" onClick={() => setPresetRange(90)}>90D</Button>
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <CalendarIcon className="h-4 w-4" />
                {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={{ from: dateRange.from, to: dateRange.to }}
                onSelect={(range) => {
                  if (range?.from && range?.to) {
                    setDateRange({ from: range.from, to: range.to });
                  }
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>

          <Button onClick={handleExport} variant="outline" size="sm" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {formatCurrency(summary?.totalRevenue || 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4" />
              Total COGS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary?.totalCogs || 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Damage Loss
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {formatCurrency(summary?.totalDamageLoss || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-primary/5 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-primary flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              CM1 (Contribution Margin)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {formatCurrency(summary?.totalCm1 || 0)}
            </div>
            <Badge 
              variant={summary?.overallMargin && summary.overallMargin >= 20 ? "default" : "destructive"}
              className="mt-1"
            >
              {summary?.overallMargin?.toFixed(1) || 0}% margin
            </Badge>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="trend" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trend">Trend Analysis</TabsTrigger>
          <TabsTrigger value="products">Product Breakdown</TabsTrigger>
          <TabsTrigger value="insights">Insights</TabsTrigger>
        </TabsList>

        {/* Trend Analysis Tab */}
        <TabsContent value="trend" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* CM1 Trend Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>CM1 Trend Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendData || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      tickFormatter={(v) => format(new Date(v), 'MMM d')}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--background))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px'
                      }}
                      formatter={(value: number) => formatCurrency(value)}
                      labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke={CHART_COLORS.revenue} 
                      strokeWidth={2}
                      name="Revenue"
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="cm1" 
                      stroke={CHART_COLORS.cm1} 
                      strokeWidth={2}
                      name="CM1"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Cost Breakdown Pie */}
            <Card>
              <CardHeader>
                <CardTitle>Cost Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={costBreakdownData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {costBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => formatCurrency(value)}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Product Breakdown Tab */}
        <TabsContent value="products">
          <Card>
            <CardHeader>
              <CardTitle>CM1 by Product</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">COGS</TableHead>
                      <TableHead className="text-right">Damage</TableHead>
                      <TableHead className="text-right">CM1</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                      <TableHead className="text-right">Qty Sold</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.product_id}>
                        <TableCell className="font-medium">{product.product_name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(product.revenue)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(product.cogs)}</TableCell>
                        <TableCell className="text-right text-destructive">
                          {product.damage_loss > 0 ? formatCurrency(product.damage_loss) : '-'}
                        </TableCell>
                        <TableCell className={cn(
                          "text-right font-medium",
                          product.cm1 >= 0 ? "text-primary" : "text-destructive"
                        )}>
                          {formatCurrency(product.cm1)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge 
                            variant={product.cm1_margin >= 20 ? "default" : product.cm1_margin >= 0 ? "secondary" : "destructive"}
                          >
                            {product.cm1_margin.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{product.quantity_sold.toFixed(1)} kg</TableCell>
                      </TableRow>
                    ))}
                    {products.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          No data available for the selected period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insights Tab */}
        <TabsContent value="insights" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Performers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <ArrowUpRight className="h-5 w-5" />
                  Top Performers (by CM1)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topPerformers.map((p, i) => (
                    <div key={p.product_id} className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-primary">#{i + 1}</span>
                        <div>
                          <p className="font-medium">{p.product_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {p.quantity_sold.toFixed(1)} kg sold
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">{formatCurrency(p.cm1)}</p>
                        <Badge variant="default">{p.cm1_margin.toFixed(1)}%</Badge>
                      </div>
                    </div>
                  ))}
                  {topPerformers.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No data</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Underperformers / Watch List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <ArrowDownRight className="h-5 w-5" />
                  Watch List (Low Margin)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {underperformers.map((p, i) => (
                    <div key={p.product_id} className="flex items-center justify-between p-3 bg-destructive/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                        <div>
                          <p className="font-medium">{p.product_name}</p>
                          <p className="text-sm text-muted-foreground">
                            Damage: {formatCurrency(p.damage_loss)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn(
                          "font-bold",
                          p.cm1 >= 0 ? "text-foreground" : "text-destructive"
                        )}>
                          {formatCurrency(p.cm1)}
                        </p>
                        <Badge variant="destructive">{p.cm1_margin.toFixed(1)}%</Badge>
                      </div>
                    </div>
                  ))}
                  {underperformers.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No data</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Insights */}
          <Card>
            <CardHeader>
              <CardTitle>Key Metrics Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Products Tracked</p>
                  <p className="text-2xl font-bold">{summary?.productCount || 0}</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Avg. COGS Rate</p>
                  <p className="text-2xl font-bold">
                    {summary?.totalRevenue 
                      ? ((summary.totalCogs / summary.totalRevenue) * 100).toFixed(1) 
                      : 0}%
                  </p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Damage Rate</p>
                  <p className="text-2xl font-bold text-destructive">
                    {summary?.totalRevenue 
                      ? ((summary.totalDamageLoss / summary.totalRevenue) * 100).toFixed(1) 
                      : 0}%
                  </p>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Overall CM1 Margin</p>
                  <p className="text-2xl font-bold text-primary">
                    {summary?.overallMargin?.toFixed(1) || 0}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CM1Report;
